import * as XLSX from "xlsx";
import type { EventResultsPayload, TeamResult } from "../api/client";
import type { ExportResultsFilenameOpts } from "./exportResults";

type JudgeRole = "judge1" | "judge2" | "judge3";

type SubCriterion = {
  label: string;
  read: (team: TeamResult) => number;
};

const JUDGE_SHEETS: {
  role: JudgeRole;
  sheet: string;
  subCriteria: SubCriterion[];
}[] = [
  {
    role: "judge1",
    sheet: "Judge 1 - Dance Skill",
    subCriteria: [
      {
        label: "Strength & Difficulty",
        read: (t) => t.judge1.strengthDifficulty,
      },
      { label: "Timing & Control", read: (t) => t.judge1.timingControl },
      {
        label: "Unity & Synchronizity",
        read: (t) => t.judge1.unitySynchronicity,
      },
    ],
  },
  {
    role: "judge2",
    sheet: "Judge 2 - Stage Presence",
    subCriteria: [
      {
        label: "Charisma & Confidence",
        read: (t) => t.judge2.charismaConfidence,
      },
      {
        label: "Facial Expression & Emotions",
        read: (t) => t.judge2.facialExpression,
      },
      {
        label: "Stage Awareness & Engagement",
        read: (t) => t.judge2.stageAwareness,
      },
    ],
  },
  {
    role: "judge3",
    sheet: "Judge 3 - Concept",
    subCriteria: [
      {
        label: "Concept & Originality",
        read: (t) => t.judge3.conceptOriginality,
      },
      {
        label: "Costume, Props & Visual Composition",
        read: (t) => t.judge3.costumeVisual,
      },
      {
        label: "Choreography Creativity & Overall Quality",
        read: (t) => t.judge3.choreographyQuality,
      },
    ],
  },
];

function judgeSubmitted(team: TeamResult, role: JudgeRole): boolean {
  if (role === "judge1") return team.judge1.submitted;
  if (role === "judge2") return team.judge2.submitted;
  return team.judge3.submitted;
}

function stemForReportFile(o: ExportResultsFilenameOpts): string {
  const raw = (
    o.eventName?.trim() ||
    o.eventSlug?.trim() ||
    "KiDS_EXPO_2026"
  ).replace(/\.xlsx$/i, "");
  let s = raw
    .replace(/[^\p{L}\p{N}._-]+/gu, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
  if (!s) s = "KiDS_EXPO_2026";
  return s.slice(0, 96);
}

export function exportScoringReportToExcel(
  payload: EventResultsPayload,
  opts: ExportResultsFilenameOpts = {},
): void {
  const wb = XLSX.utils.book_new();

  for (const judge of JUDGE_SHEETS) {
    const extraColumns = new Map<string, string>();
    for (const section of payload.divisions) {
      for (const round of section.rounds) {
        for (const team of round.teams) {
          for (const c of team.extraCriteria) {
            if (
              c.judgeRole === judge.role &&
              !extraColumns.has(c.criterionId)
            ) {
              extraColumns.set(c.criterionId, `★ ${c.name} (/${c.maxScore})`);
            }
          }
        }
      }
    }

    const rows: Record<string, string | number>[] = [];
    for (const section of payload.divisions) {
      for (const round of section.rounds) {
        for (const team of round.teams) {
          const extraByCriterion = new Map(
            team.extraCriteria.map((c) => [c.criterionId, c.value] as const),
          );
          const row: Record<string, string | number> = {
            ຮາງ: section.divisionName,
            ຮອບ: round.roundIndex,
            ລຳດັບ: team.orderNumber,
            ຊື່ທີມ: team.name,
          };
          let total = 0;
          for (const sub of judge.subCriteria) {
            const v = sub.read(team);
            row[sub.label] = v;
            total += v;
          }
          for (const [criterionId, header] of extraColumns) {
            const v = extraByCriterion.get(criterionId) ?? 0;
            row[header] = v;
            total += v;
          }
          row["ລວມ"] = total;
          row["ສະຖານະ"] = judgeSubmitted(team, judge.role) ? "ສົ່ງແລ້ວ" : "ຍັງ";
          rows.push(row);
        }
      }
    }

    const ws = XLSX.utils.json_to_sheet(rows);
    if (rows.length > 0) {
      ws["!cols"] = Object.keys(rows[0] ?? {}).map((key) => ({
        wch: Math.max(key.length + 2, 12),
      }));
    }
    XLSX.utils.book_append_sheet(wb, ws, judge.sheet);
  }

  if (wb.SheetNames.length === 0) {
    const ws = XLSX.utils.json_to_sheet([]);
    XLSX.utils.book_append_sheet(wb, ws, "ລາຍງານ");
  }

  XLSX.writeFile(wb, `${stemForReportFile(opts)}_Scoring_Report.xlsx`);
}
